export const locales = ["en", "pt-BR", "es", "id", "th", "de"] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = "en";

/**
 * Locales allowed into the search index — the sitemap, the hreflang cluster and the absence of a
 * `noindex`. A subset of `locales`, which stays the full roster: every locale here keeps its
 * routes, its language-switcher entry and its translated content. This flag governs only what we
 * ask Google to index.
 *
 * Why the two lists differ (2026-08-25). Search Console on 2026-08-23: 6,168 URLs submitted,
 * 2,540 indexed, and 3,040 sitting in "Rastreada/Detectada, mas não indexada" — i.e. Google
 * crawled roughly half the site and declined it. Every URL in that report's sample, crawled
 * 21-22/08, was a non-English locale page (/id/flowers/cranesbill, /de/crops/eggplant,
 * /th/insects/sulkowskys-morpho...). Impressions had collapsed 1,623 -> 86/day between 15/08 and
 * 23/08 while average position fell from ~10 to 23.6, leaving only branded queries — the shape of
 * a sitewide quality demotion, not a technical fault (all URLs 200, canonicals self-referencing,
 * Googlebot crawling normally, no manual action).
 *
 * de/id/th launched on 2026-08-20 and tripled the submitted URL count in three weeks. They have
 * never produced a single click, so withholding them costs no traffic; pt-BR and es stay because
 * they demonstrably earn it (/pt-br/events, /es/weather were top-10 pages over the last 28 days).
 *
 * Reintroduce ONE locale at a time and let the indexed/not-indexed ratio settle before the next.
 * Putting all three back at once is what got the site here.
 */
export const INDEXABLE_LOCALES: readonly Locale[] = ["en", "pt-BR", "es"];

export const isIndexableLocale = (locale: Locale): boolean => INDEXABLE_LOCALES.includes(locale);

/** Narrow an hreflang cluster to the locales we actually ask Google to index. */
export const indexableOnly = (candidates: readonly Locale[]): Locale[] => candidates.filter(isIndexableLocale);

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  "pt-BR": "Português",
  es: "Español",
  id: "Bahasa Indonesia",
  th: "ไทย",
  de: "Deutsch",
};

const normalize = (value: string) => value.toLowerCase().replace("_", "-");

export const isLocale = (value: string | undefined | null): value is Locale =>
  !!value && (locales as readonly string[]).includes(value);

export function resolveLocale(value: string | undefined | null): Locale | null {
  if (!value) return null;
  if (isLocale(value)) return value;
  const normalized = normalize(value);
  return locales.find((locale) => normalize(locale) === normalized) ?? null;
}

/** URL segment for a locale: default locale has no prefix, others use a lowercase slug (pt-BR -> pt-br). */
export function localeUrlSlug(locale: Locale): string {
  return locale === defaultLocale ? "" : normalize(locale);
}
